import { autoDetectRenderer, Container } from 'pixi.js';
import { AssetManager } from '../assets/AssetManager';
import { PositionalAudio } from '../audio/PositionalAudio';
import { Camera } from '../camera/Camera';
import { DrawCallCounter, emptyStats, PerfOverlay } from '../debug/PerfOverlay';
import { InputManager } from '../input/InputManager';
import { effectiveResolution, FrameBudgetWatchdog } from '../quality/Quality';
import { Lighting } from '../render/Lighting';
import { FixedStepLoop } from './FixedStepLoop';
/**
 * Engine facade: owns the renderer, the fixed-step loop, camera, input,
 * render layers, lighting, assets and stats. Game code builds an IsoScene,
 * hands it over with setScene(), and hooks into onFixedUpdate / onFrame.
 *
 * Stage layout (back to front):
 *   stage
 *   ├─ worldRoot (camera transform; tinted by ambient light in tint mode)
 *   │  ├─ ground  (render group: floor chunks + decals)       <- scene.floor
 *   │  ├─ world   (render group: depth-sorted items)          <- scene.world
 *   │  ├─ overlay (markers, selection, speech-bubble anchors)
 *   │  └─ fx      (additive glows, particles)
 *   └─ lightmap   (screen-space multiply sprite, med/high tiers)
 */
export class Engine {
    renderer;
    stage = new Container({ label: 'stage' });
    worldRoot = new Container({ label: 'worldRoot' });
    sceneSlot = new Container({ label: 'sceneSlot' });
    overlay = new Container({ label: 'overlay' });
    fx = new Container({ label: 'fx' });
    camera;
    input;
    loop;
    assets;
    lighting;
    audio = new PositionalAudio();
    perf;
    stats = emptyStats();
    quality;
    /** Game input callbacks (screen CSS px). Mutable so game code can wire them after create(). */
    handlers = {};
    scene = null;
    parent;
    fixedHooks = [];
    frameHooks = [];
    drawCounter;
    watchdog;
    view = { left: 0, top: 0, right: 0, bottom: 0 };
    dpr = 1;
    resolution = 1;
    elapsed = 0;
    // Stat accumulators (reported every 500 ms).
    acc = { frames: 0, frameMs: 0, frameMax: 0, sim: 0, prep: 0, sort: 0, render: 0, draws: 0, t0: 0 };
    simMsThisFrame = 0;
    onResizeBound = () => this.resize();
    onVisibility = () => {
        if (document.hidden)
            this.loop.stop();
        else
            this.loop.start();
    };
    resizeObserver = null;
    constructor(renderer, opts) {
        this.renderer = renderer;
        this.parent = opts.parent;
        this.quality = opts.quality;
        this.camera = new Camera({ minZoom: opts.quality.minZoom, maxZoom: 2.5 });
        this.assets = new AssetManager(renderer, opts.quality.artScale);
        this.worldRoot.addChild(this.sceneSlot, this.overlay, this.fx);
        this.stage.addChild(this.worldRoot);
        this.lighting = new Lighting(renderer, {
            lightmap: opts.quality.lightmap,
            lightmapScale: opts.quality.lightmapScale,
            worldRoot: this.worldRoot,
            glowLayer: this.fx,
        });
        this.stage.addChild(this.lighting.overlay);
        // We hit-test with iso maths, not Pixi's federated events.
        this.stage.eventMode = 'none';
        this.stage.interactiveChildren = false;
        const canvas = renderer.canvas;
        canvas.style.display = 'block';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        opts.parent.appendChild(canvas);
        const h = this.handlers;
        this.input = new InputManager(canvas, {
            onTap: (x, y, type) => h.onTap?.(x, y, type),
            onPan: (dx, dy) => h.onPan?.(dx, dy),
            onZoom: (f, x, y) => h.onZoom?.(f, x, y),
            onHover: (x, y) => h.onHover?.(x, y),
        });
        this.perf = new PerfOverlay(opts.parent, !!opts.debug);
        this.drawCounter = new DrawCallCounter(renderer);
        this.watchdog = opts.adaptiveResolution === false ? null : new FrameBudgetWatchdog(opts.frameBudgetMs ?? 1000 / 60, opts.quality.minResolutionScale);
        this.stats.tier = opts.quality.tier;
        this.stats.renderer = renderer.name;
        this.loop = new FixedStepLoop({
            fixedUpdate: (dt, tick) => this.fixedUpdate(dt, tick),
            render: (alpha, frameDt, now) => this.renderFrame(alpha, frameDt, now),
        });
        window.addEventListener('resize', this.onResizeBound);
        window.addEventListener('orientationchange', this.onResizeBound);
        window.visualViewport?.addEventListener('resize', this.onResizeBound);
        document.addEventListener('visibilitychange', this.onVisibility);
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.onResizeBound);
            this.resizeObserver.observe(opts.parent);
        }
        this.resize();
    }
    /** Creates the renderer (async in Pixi v8) and the engine around it. */
    static async create(opts) {
        const dpr = window.devicePixelRatio || 1;
        const renderer = await autoDetectRenderer({
            preference: opts.preference ?? 'webgl',
            width: Math.max(1, opts.parent.clientWidth),
            height: Math.max(1, opts.parent.clientHeight),
            resolution: effectiveResolution(dpr, opts.quality, 1),
            autoDensity: true,
            antialias: false,
            background: opts.background ?? 0x000000,
            powerPreference: 'high-performance',
            eventFeatures: { move: false, globalMove: false, click: false, wheel: false },
        });
        return new Engine(renderer, opts);
    }
    setScene(scene) {
        if (this.scene) {
            this.sceneSlot.removeChildren();
        }
        this.scene = scene;
        scene.pathBudgetPerTick = this.quality.pathBudgetPerTick;
        this.sceneSlot.addChild(scene.floor.container, scene.world.container);
    }
    onFixedUpdate(fn) {
        this.fixedHooks.push(fn);
    }
    onFrame(fn) {
        this.frameHooks.push(fn);
    }
    start() {
        this.acc.t0 = performance.now();
        this.loop.start();
    }
    /** Current renderer resolution (device px per CSS px). */
    get currentResolution() {
        return this.resolution;
    }
    /** Screen (CSS px) -> world-screen px. */
    screenToWorld(sx, sy, out) {
        return this.camera.screenToWorld(sx, sy, out);
    }
    resize() {
        const w = Math.max(1, this.parent.clientWidth);
        const h = Math.max(1, this.parent.clientHeight);
        this.dpr = window.devicePixelRatio || 1;
        this.resolution = effectiveResolution(this.dpr, this.quality, this.watchdog?.scale ?? 1);
        this.renderer.resize(w, h, this.resolution);
        this.camera.setViewport(w, h);
        this.lighting.resize(w, h);
        this.stats.resolution = this.resolution;
    }
    fixedUpdate(dt, tick) {
        const t0 = performance.now();
        this.scene?.fixedUpdate(dt);
        for (const f of this.fixedHooks)
            f(dt, tick);
        this.simMsThisFrame += performance.now() - t0;
    }
    renderFrame(alpha, frameDt, now) {
        const tPrep = performance.now();
        this.elapsed += frameDt;
        for (const f of this.frameHooks)
            f(frameDt, alpha, now);
        const cam = this.camera;
        cam.apply(this.worldRoot, this.resolution);
        // Cull margin: half a tile around the screen so sprites never pop at the edge.
        cam.viewRect(32, this.view);
        let floorTiles = 0;
        if (this.scene) {
            this.scene.renderUpdate(alpha, frameDt, this.view);
            floorTiles = this.scene.floor.cull(this.view);
        }
        this.lighting.update(cam, this.elapsed);
        this.audio.halfViewPx = cam.viewW / 2 / cam.zoom;
        this.audio.update();
        const tRender = performance.now();
        if (this.drawCounter)
            this.drawCounter.count = 0;
        this.renderer.render({ container: this.stage });
        const tEnd = performance.now();
        this.collectStats(frameDt * 1000, tRender - tPrep, tEnd - tRender, floorTiles);
    }
    collectStats(frameMs, prepMs, renderMs, floorTiles) {
        const a = this.acc;
        const scene = this.scene;
        a.frames++;
        a.frameMs += frameMs;
        if (frameMs > a.frameMax)
            a.frameMax = frameMs;
        a.sim += this.simMsThisFrame;
        this.simMsThisFrame = 0;
        const sortMs = scene ? scene.world.stats.sortMs : 0;
        a.sort += sortMs;
        a.prep += prepMs;
        a.render += renderMs;
        a.draws += this.drawCounter?.supported ? this.drawCounter.count + this.lighting.passes : 0;
        if (this.watchdog && this.watchdog.push(frameMs))
            this.resize();
        const now = performance.now();
        const span = now - a.t0;
        if (span < 500)
            return;
        const s = this.stats;
        const n = a.frames || 1;
        s.fps = (a.frames * 1000) / span;
        s.frameMs = a.frameMs / n;
        s.frameMsMax = a.frameMax;
        s.simMs = a.sim / n;
        s.prepMs = a.prep / n;
        s.sortMs = a.sort / n;
        s.renderMs = a.render / n;
        s.drawCalls = this.drawCounter?.supported ? Math.round(a.draws / n) : -1;
        s.floorTiles = floorTiles;
        s.lightPasses = this.lighting.passes;
        s.droppedSteps = this.loop.droppedSteps;
        s.textureMB = this.assets.gpuBytes / (1024 * 1024);
        if (scene) {
            const ws = scene.world.stats;
            s.entities = ws.total;
            s.characters = scene.characters.length;
            s.worldOnScreen = ws.onScreen;
            s.sortPairs = ws.pairTests;
            s.sortEdges = ws.edges;
            let sprites = 0;
            scene.world.forEach((it) => {
                if (!it.onScreen)
                    return;
                sprites += it.kind === 'character' ? it.owner.doll.spriteCount : 1;
            });
            s.visibleSprites = sprites;
        }
        this.perf.update(s);
        a.frames = 0;
        a.frameMs = 0;
        a.frameMax = 0;
        a.sim = 0;
        a.prep = 0;
        a.sort = 0;
        a.render = 0;
        a.draws = 0;
        a.t0 = now;
    }
    destroy() {
        this.loop.stop();
        window.removeEventListener('resize', this.onResizeBound);
        window.removeEventListener('orientationchange', this.onResizeBound);
        window.visualViewport?.removeEventListener('resize', this.onResizeBound);
        document.removeEventListener('visibilitychange', this.onVisibility);
        this.resizeObserver?.disconnect();
        this.input.destroy();
        this.lighting.destroy();
        this.perf.el.remove();
        this.stage.destroy({ children: true });
        this.renderer.destroy({ removeView: true, releaseGlobalResources: true });
    }
}
